import { toast } from 'react-toastify';

import { cloneDeep, removeNullAttr, toastError, tryParseJSON } from '../../../../utils';
import { callApi } from '../../../../apiCaller';

import { CERTIFICATION_ACTION_TYPE, DEFAULT_FN, POPUP_TYPE, SYSTEM_ADMIN_PATH, USER_CERTIFICATION_LEVEL_MODEL } from '../../../../constants';

import {
     setIsLoading,
     setIsWaitingOrder,
     setRemoveLoading,
     setShow,
     setShowRelatedObjectWarningPopup,
     setShowRemovePopup,
} from '../../../../actions/general';
import { updateAdminUserDetails } from '../Users/actions';

export const SET_ADMIN_DESIGNATION_ELEMENTS = 'setAdminDesignationElements';
export const ADD_ADMIN_DESIGNATION_ELEMENT = 'addAdminDesignationElement';
export const UPDATE_ADMIN_DESIGNATION_ELEMENT = 'updateAdminDesignationElement';
export const DELETE_ADMIN_DESIGNATION_ELEMENT = 'deleteAdminDesignationElement';

const API_SYSTEM_ADMIN_DESIGNATION_ELEMENTS = 'designation-element';
const API_SYSTEM_ADMIN_DESIGNATION_ELEMENT = 'designation-element/:id';
const API_SYSTEM_ADMIN_DESIGNATION_ELEMENT_DELETE_CHECKING = 'designation-element/delete-checking/:id';
const API_SYSTEM_ADMIN_USER_DESIGNATION_ELEMENT = 'user/designation-elements/:userId';
const API_SYSTEM_ADMIN_USER_DESIGNATION_ELEMENT_CERTIFICATION = 'user/designation-elements/certification/:type/:userId';

export const setAdminDesignationElements = (designationElements) => {
     return {
          type: SET_ADMIN_DESIGNATION_ELEMENTS,
          payload: designationElements,
     };
};

const addAdminDesignationElement = (designationElement) => {
     return {
          type: ADD_ADMIN_DESIGNATION_ELEMENT,
          payload: designationElement,
     };
};

const updateAdminDesignationElement = (designationElement) => {
     return {
          type: UPDATE_ADMIN_DESIGNATION_ELEMENT,
          payload: designationElement,
     };
};

const deleteAdminDesignationElement = (designationElementId) => {
     return {
          type: DELETE_ADMIN_DESIGNATION_ELEMENT,
          payload: designationElementId,
     };
};

export const fetchDesignationElementRequest = (designationElementId, setDesignationElement) => (dispatch) => {
     dispatch(setIsLoading(true));

     callApi(API_SYSTEM_ADMIN_DESIGNATION_ELEMENT.replace(':id', designationElementId), 'GET')
          .then((response) => {
               if (response.status !== 200) {
                    toastError(response);
                    return;
               }

               const designationElement = removeNullAttr(response.data);

               designationElement.userFieldsUpdatedWhenExpiration = tryParseJSON(designationElement.userFieldsUpdatedWhenExpiration) || [];

               setDesignationElement(designationElement);
          })
          .finally(() => {
               dispatch(setIsLoading(false));
          });
};

export const fetchDesignationElementsRequest = () => (dispatch) => {
     dispatch(setIsLoading(true));

     callApi(API_SYSTEM_ADMIN_DESIGNATION_ELEMENTS, 'GET')
          .then((response) => {
               if (response.status === 200) {
                    dispatch(setAdminDesignationElements(response.data));
               } else {
                    toastError(response);
               }
          })
          .finally(() => {
               dispatch(setIsLoading(false));
          });
};

export const addAdminDesignationElementRequest = (designationElement, navigate) => async (dispatch) => {
     return await callApi(API_SYSTEM_ADMIN_DESIGNATION_ELEMENTS, 'POST', designationElement).then((response) => {
          if (response.status !== 200) {
               toastError(response);
               return;
          }

          const newDesignation = response.data;

          dispatch(addAdminDesignationElement(newDesignation));
          navigate(SYSTEM_ADMIN_PATH.DESIGNATION_ELEMENTS_DETAIL.replace(':id', newDesignation.id));

          toast.success('Created successfully');
          return newDesignation;
     });
};

export const updateAdminDesignationElementRequest = (designationElementId, designationElement) => async (dispatch, getState) => {
     const designationElements = getState().admin.designationElements;

     return await callApi(API_SYSTEM_ADMIN_DESIGNATION_ELEMENT.replace(':id', designationElementId), 'PUT', designationElement).then((response) => {
          if (response.status !== 200) {
               dispatch(setShow(null));
               toastError(response);
               return;
          }

          const designationUpdated = response.data;
          if (designationElements.length) {
               dispatch(updateAdminDesignationElement(designationUpdated));
          }

          toast.success('Update successfully');
          return designationUpdated;
     });
};

export const checkDeleteAdminDesignationRequest = (designationElementId) => (dispatch) => {
     callApi(API_SYSTEM_ADMIN_DESIGNATION_ELEMENT_DELETE_CHECKING.replace(':id', designationElementId), 'GET').then((response) => {
          if (response.status === 200 && response.data.canDelete) {
               dispatch(setShowRemovePopup(designationElementId));
               return;
          }

          if (!response.data.canDelete) {
               dispatch(setShowRelatedObjectWarningPopup(POPUP_TYPE.WARNING_RELATED, response.data.message));
          } else {
               toastError(response);
          }
     });
};

export const proceedRemoveAdminDesignationElementRequest = (designationElementId, navigate) => (dispatch, getState) => {
     const designationElements = getState().admin.designationElements;

     dispatch(setRemoveLoading(true));

     callApi(API_SYSTEM_ADMIN_DESIGNATION_ELEMENT.replace(':id', designationElementId), 'DELETE')
          .then((response) => {
               if (response.status !== 200) {
                    toastError(response);
                    return;
               }

               if (designationElements.length) {
                    dispatch(deleteAdminDesignationElement(designationElementId));
               }

               if (navigate) {
                    navigate(SYSTEM_ADMIN_PATH.DESIGNATION_ELEMENTS, { replace: true });
               }

               toast.success('Delete successfully');
          })
          .finally(() => {
               dispatch(setShowRemovePopup(null));
               dispatch(setRemoveLoading(false));
          });
};

export const orderDesignationElements = (action, designationElement, swapPosition) => (dispatch, getState) => {
     const designationElements = getState().admin.designationElements;

     const _designationElements = cloneDeep(designationElements);
     const index = _designationElements.findIndex((object) => object.id === designationElement.id);

     dispatch(setIsWaitingOrder(true));

     if (action === 'up' && index > 0) {
          [_designationElements[index], _designationElements[index - 1]] = [_designationElements[index - 1], designationElement];
          [_designationElements[index].order, _designationElements[index - 1].order] = [
               _designationElements[index - 1].order,
               _designationElements[index].order,
          ];

          dispatch(setAdminDesignationElements(_designationElements));
          swapPosition(_designationElements[index], _designationElements[index - 1]);
          return;
     }

     if (action === 'down' && index + 1 < _designationElements.length) {
          [_designationElements[index], _designationElements[index + 1]] = [_designationElements[index + 1], designationElement];
          [_designationElements[index].order, _designationElements[index + 1].order] = [
               _designationElements[index + 1].order,
               _designationElements[index].order,
          ];

          dispatch(setAdminDesignationElements(_designationElements));
          swapPosition(_designationElements[index], _designationElements[index + 1]);
     }
};

export const swapPositionDesignationElements = (designationElement1, designationElement2) => (dispatch) => {
     let waitingOrderCount = 0;

     callApi(API_SYSTEM_ADMIN_DESIGNATION_ELEMENT.replace(':id', designationElement1.id), 'PUT', designationElement1)
          .then((response) => {
               if (response.status !== 200) {
                    toastError(response);
               }
          })
          .finally(() => {
               if (waitingOrderCount > 0) {
                    dispatch(setIsWaitingOrder(false));
               } else {
                    waitingOrderCount++;
               }
          });

     callApi(API_SYSTEM_ADMIN_DESIGNATION_ELEMENT.replace(':id', designationElement2.id), 'PUT', designationElement2)
          .then((response) => {
               if (response.status !== 200) {
                    toastError(response);
               }
          })
          .finally(() => {
               if (waitingOrderCount > 0) {
                    dispatch(setIsWaitingOrder(false));
               } else {
                    waitingOrderCount++;
               }
          });
};

// User designation element
export const fetchUserDesignationElementsRequest =
     (userId, setUserDesignationElement = DEFAULT_FN, setUserDesignationElementInit = DEFAULT_FN) =>
     (dispatch) => {
          dispatch(setIsLoading(true));

          callApi(API_SYSTEM_ADMIN_USER_DESIGNATION_ELEMENT.replace(':userId', userId), 'GET')
               .then((response) => {
                    if (response.status !== 200) {
                         toastError(response);
                         return;
                    }

                    const userDesignationRes = response.data;

                    setUserDesignationElement(userDesignationRes);
                    setUserDesignationElementInit(cloneDeep(userDesignationRes));
               })
               .finally(() => {
                    dispatch(setIsLoading(false));
               });
     };

export const updateUserDesignationElementsRequest = (userId, userDesignationPayload, nextDispatch) => (dispatch, useSelector) => {
     const currentUser = useSelector().admin.userDetails;

     const { setIsSubmitting, setUserDesignationElement, setUserDesignationElementInit } = nextDispatch;
     setIsSubmitting(true);

     callApi(API_SYSTEM_ADMIN_USER_DESIGNATION_ELEMENT.replace(':userId', userId), 'PUT', userDesignationPayload).then((response) => {
          setIsSubmitting(false);

          if (response.status !== 200) {
               toastError(response);
               return;
          }

          const userDesignationRes = response.data;
          setUserDesignationElement(userDesignationRes);
          setUserDesignationElementInit(cloneDeep(userDesignationRes));

          const certificateLevel = USER_CERTIFICATION_LEVEL_MODEL[userDesignationRes.certificationLevel.level] || 'none';
          if (currentUser?.certificateLevel !== certificateLevel) {
               dispatch(updateAdminUserDetails({ certificateLevel }));
          }

          toast.success('Update successfully');
     });
};

export const updateAndClearUserCertificationRequest = (userId, type, nextDispatch) => (dispatch, useSelector) => {
     const show = useSelector().general.show;
     const currentUser = useSelector().admin.userDetails;

     const { setIsSubmitting, setUserDesignationElement, setUserDesignationElementInit } = nextDispatch;

     setIsSubmitting(true);
     callApi(API_SYSTEM_ADMIN_USER_DESIGNATION_ELEMENT_CERTIFICATION.replace(':type', type).replace(':userId', userId), 'PUT').then((response) => {
          setIsSubmitting(false);

          if (show !== null) {
               dispatch(setShow(null));
          }

          if (response.status !== 200) {
               toastError(response);
               return;
          }

          const userDesignationRes = response.data;

          // If there is data, return data, otherwise return false
          if (userDesignationRes) {
               setUserDesignationElement(userDesignationRes);
               setUserDesignationElementInit(cloneDeep(userDesignationRes));

               const certificateLevel = USER_CERTIFICATION_LEVEL_MODEL[userDesignationRes.certificationLevel.level] || 'none';

               if (currentUser?.certificateLevel !== certificateLevel) {
                    dispatch(updateAdminUserDetails({ certificateLevel }));
               }
          }

          switch (type) {
               case CERTIFICATION_ACTION_TYPE.CLEAR:
                    toast.success('Clear certification successfully');
                    break;

               case CERTIFICATION_ACTION_TYPE.UPDATE:
                    toast.success('Update certification successfully');
                    break;

               default:
                    break;
          }
     });
};
